import { TFunction } from 'i18next';
import { isProjectActive } from './project';

export type TaskStatusConfig = {
  code: string;
  getLabel: (t: TFunction) => string;
  color: string;

  width: number;

  displayOnSelect: boolean;
  disableIfSelected?: boolean;
  canAddSubTask: boolean;
  onlyShowOnKanbanIfPresent: boolean;
  isActiveStatus: boolean;
  canCancel: boolean;
  canChangeDate: boolean;
  canChangeOwner: boolean;
  canChangeSubOwner: boolean;
  canChangeName: boolean;

  canUpdateSampleIds: boolean;
  canCreateSampleProject: boolean;
  canRequestRecruitment: boolean;

  canChangeStatus: boolean;

  blocks: {
    fullBlock?: boolean;
    addSubtasks?: boolean;
    updateIds?: boolean;
    changeSubStatus?: boolean;
  };

  order: number;

  changes?: {
    getTo?: (t: TFunction) => string;
    getFrom?: (t: TFunction) => string;
  };
};

export const taskStatusesConfig = {
  done: {
    code: '0',
    getLabel: (t: TFunction) => t('done'),
    color: '#00D39D',
    width: 100,
    displayOnSelect: true,
    canAddSubTask: false,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: false,
    canCancel: false,
    canChangeDate: false,
    canChangeOwner: false,
    canChangeSubOwner: false,
    canChangeName: false,
    canChangeStatus: true,
    canUpdateSampleIds: false,
    canCreateSampleProject: false,
    canRequestRecruitment: false,

    changes: {
      getFrom: (t: TFunction) => t('why-is-this-task-not-complete anymore') as string,
    },

    order: 5,

    blocks: {
      addSubtasks: true,
      updateIds: true,
      changeSubStatus: true,
    },
  },

  unassigned: {
    code: '1',
    getLabel: (t: TFunction) => t('unassigned'),
    color: '#FC8A05',
    width: 150,
    displayOnSelect: false,
    disableIfSelected: true,
    canAddSubTask: false,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: true,
    canCancel: true,
    canChangeDate: false,
    canChangeOwner: true,
    canChangeSubOwner: true,
    canChangeName: false,
    canChangeStatus: false,
    canUpdateSampleIds: false,
    canCreateSampleProject: false,
    canRequestRecruitment: false,

    order: 0,

    blocks: {
      addSubtasks: true,
      updateIds: true,
    },
  },

  notStated: {
    code: '2',
    getLabel: (t: TFunction) => t('not-started'),
    color: '#C4C4C4',
    width: 150,
    displayOnSelect: true,
    canAddSubTask: true,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: true,
    canCancel: true,
    canChangeDate: true,
    canChangeOwner: true,
    canChangeSubOwner: true,
    canChangeName: true,
    canChangeStatus: true,
    canUpdateSampleIds: false,
    canCreateSampleProject: false,
    canRequestRecruitment: false,

    order: 1,

    blocks: {},
  },

  pendingApproval: {
    code: '3',
    getLabel: (t: TFunction) => t('pending-approval'),
    color: '#FF007D',
    width: 210,
    displayOnSelect: false,
    disableIfSelected: true,
    canAddSubTask: true,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: true,
    canCancel: false,
    canChangeDate: false,
    canChangeOwner: true,
    canChangeSubOwner: true,
    canChangeName: false,
    canChangeStatus: false,
    canUpdateSampleIds: true,
    canCreateSampleProject: false,
    canRequestRecruitment: false,

    order: 2,

    blocks: {},
  },

  workingOnIt: {
    code: '4',
    getLabel: (t: TFunction) => t('started'),
    color: '#1560CE',
    width: 100,
    displayOnSelect: true,
    canAddSubTask: true,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: true,
    canCancel: true,
    canChangeDate: true,
    canChangeOwner: true,
    canChangeSubOwner: true,
    canChangeName: true,
    canChangeStatus: true,
    canUpdateSampleIds: true,
    canCreateSampleProject: true,
    canRequestRecruitment: true,

    order: 3,

    blocks: {},
  },

  stuck: {
    code: '5',
    getLabel: (t: TFunction) => t('stuck'),
    color: '#FFBF00',
    width: 140,
    displayOnSelect: true,
    canAddSubTask: true,
    onlyShowOnKanbanIfPresent: false,
    isActiveStatus: true,
    canCancel: true,
    canChangeDate: true,
    canChangeOwner: true,
    canChangeSubOwner: true,
    canChangeName: true,
    canChangeStatus: true,
    canUpdateSampleIds: true,
    canCreateSampleProject: true,
    canRequestRecruitment: true,

    changes: {
      getTo: (t: TFunction) => t('why-is-your-task-stuck') as string,
    },

    order: 4,

    blocks: {},
  },

  canceled: {
    code: '9',
    getLabel: (t: TFunction) => t('canceled'),
    color: '#F3003A',
    width: 150,
    displayOnSelect: false,
    canAddSubTask: false,
    onlyShowOnKanbanIfPresent: true,
    isActiveStatus: false,
    canCancel: false,
    canChangeDate: false,
    canChangeOwner: false,
    canChangeSubOwner: false,
    canChangeName: false,
    canChangeStatus: false,
    canUpdateSampleIds: false,
    canCreateSampleProject: false,
    canRequestRecruitment: false,

    order: 6,

    blocks: {
      fullBlock: true,
      addSubtasks: true,
      updateIds: true,
    },
  },
};

export const taskStatusByCode = Object.fromEntries(
  Object.values(taskStatusesConfig).map((config) => [config.code, config]),
) as Record<string, TaskStatusConfig>;

export const getTaskStatusConfig = (status: string): TaskStatusConfig => {
  return taskStatusByCode[status] as TaskStatusConfig;
};

export function isTaskActive(
  projectRef: { status: string } | string,
  taskRef: { status: string } | string,
) {
  const status = typeof taskRef === 'string' ? taskRef : taskRef?.status;

  return isProjectActive(projectRef) && !!taskStatusByCode[status]?.isActiveStatus;
}
